import { type FC, useRef } from "react";

import { CityField, CountryField } from "@ui/components/AddressFormProvider";
import { Controller, useFormContext } from "@ui/components/Form";
import { TextInput } from "@ui/components/Inputs";
import { type PlaceInputResults } from "@ui/components/PlaceInput";
import { PostCode } from "@ui/components/PostCode";
import { StreetAddressFields } from "@ui/components/StreetAddressFields";
import { useTranslations } from "@ui/providers/useTranslations";
import { getDeliveryLimitCitiesDropdownListV2 } from "@ui/utils/getDeliveryLimitCitiesDropdownListV2/getDeliveryLimitCitiesDropdownListV2";
import { getProvinceCodeTranslationKey } from "@ui/utils/getProvinceCodeTranslationKey";

import type { LocationServiceAutocompleteService } from "@jti/fe-common";
import type { IGetOrderDeliveryLimits, PostCodeConfig } from "@jti/magento";

import styles from "./ExtendedWithProvinceForm.module.scss";

type ExtendedWithProvinceFormProps = {
  fieldName?(name: string): string;
  enableStreetNameAutocomplete?: boolean;
  orderDeliveryLimits?: IGetOrderDeliveryLimits;
  postCodeConfig?: PostCodeConfig;
  locationServiceAutocompleteProvider?: LocationServiceAutocompleteService;
  isStreetFieldCombined?: boolean;
};

export const ExtendedWithProvinceForm: FC<ExtendedWithProvinceFormProps> = ({
  fieldName = (name) => name,
  enableStreetNameAutocomplete,
  orderDeliveryLimits,
  postCodeConfig,
  locationServiceAutocompleteProvider,
  isStreetFieldCombined,
}) => {
  const { __ } = useTranslations();
  const { getValues, trigger, control, setValue } = useFormContext();
  const isCityLimitationEnabled = orderDeliveryLimits?.mode === "yes_city";
  const deliveryLimitCitiesDropdownList = getDeliveryLimitCitiesDropdownListV2(orderDeliveryLimits?.limits);
  const initialProvinceCode = useRef<string>(getValues("_hiddenValues.immutableProvinceCode"));

  const handleOnUpdateStreet = (place: PlaceInputResults) => {
    setValue("_hiddenValues.immutableProvinceCode", place.regionCode);
    trigger();
  };

  return (
    <div className={styles.wrapper}>
      <StreetAddressFields
        fieldName={fieldName}
        onUpdateStreet={handleOnUpdateStreet}
        enableStreetNameAutocomplete={enableStreetNameAutocomplete}
        locationServiceAutocompleteProvider={locationServiceAutocompleteProvider}
        isStreetFieldCombined={isStreetFieldCombined}
        ignoredAutoUpdates={["regionCode", "provinceName"]}
      />

      <div className={styles.addressFields}>
        <CityField
          fieldName={fieldName}
          isCityLimitationEnabled={isCityLimitationEnabled}
          deliveryLimitCitiesDropdownList={deliveryLimitCitiesDropdownList}
          orderDeliveryLimits={orderDeliveryLimits}
        />
        <Controller
          control={control}
          name="_hiddenValues.immutableProvinceCode"
          render={({ field: controllerField, fieldState }) => (
            <TextInput
              {...controllerField}
              value={
                controllerField.value
                  ? __(getProvinceCodeTranslationKey(controllerField.value), [], controllerField.value)
                  : ""
              }
              aria-labelledby="label-custom_attributes.province"
              placeholder={__("address_fields.province", [], "")}
              label={__("address_fields.province", [], "")}
              labelId="label-custom_attributes.province"
              isError={!!fieldState.error}
              errorText={fieldState?.error?.message}
              required
              disabled
              autoComplete="address-level1"
            />
          )}
          rules={{
            required: true,
            validate: (value) =>
              value === initialProvinceCode.current ? true : __("address_fields.province.change_not_allowed"),
          }}
        />

        <PostCode fieldName={fieldName} config={postCodeConfig} />
      </div>

      <CountryField fieldName={fieldName} />
    </div>
  );
};
